import * as THREE from 'three'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader'
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader'
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader'
import type { ModelFormat, ModelLoadResult } from './types'
import { computeMeasurements } from './MeasurementUtils'

export interface ModelLoadOptions {
  url: string
  format?: ModelFormat
  autoCenter?: boolean
  autoScale?: boolean
  targetSize?: number
  onProgress?: (percent: number) => void
}

const cache = new Map<string, Promise<ModelLoadResult>>()

let modelCount = 0

function detectFormat(url: string): ModelFormat {
  const m = url.match(/\.(\w+)(?:\?|$)/)
  const ext = m?.[1]?.toLowerCase() || ''
  if (ext === 'stl') return 'stl'
  if (ext === 'obj') return 'obj'
  if (ext === 'ply') return 'ply'
  if (ext === 'gltf') return 'gltf'
  if (ext === 'glb') return 'glb'
  if (ext === 'fbx') return 'fbx'
  if (ext === 'step' || ext === 'stp') return 'step'
  if (ext === 'iges' || ext === 'igs') return 'iges'
  if (ext === 'brep') return 'brep'
  throw new Error(`unsupported format .${ext}, need .stl .obj .ply .gltf .glb .fbx .step .stp .iges .igs .brep`)
}

function applyFit(group: THREE.Group, center: boolean, scale: boolean, targetSize: number) {
  const box = new THREE.Box3().setFromObject(group)
  if (box.isEmpty()) return
  if (scale) {
    const size = box.getSize(new THREE.Vector3())
    const maxDim = Math.max(size.x, size.y, size.z)
    if (maxDim > 0) {
      group.scale.setScalar(targetSize / maxDim)
    }
  }
  if (center) {
    const c = box.getCenter(new THREE.Vector3())
    group.position.set(-c.x * group.scale.x, -c.y * group.scale.y, -c.z * group.scale.z)
  }
}

function countFaces(obj: THREE.Object3D): number {
  let n = 0
  obj.traverse(c => {
    if (c instanceof THREE.Mesh && c.geometry) {
      const g = c.geometry
      n += g.index ? g.index.count / 3 : g.attributes.position.count / 3
    }
  })
  return Math.floor(n)
}

function countVertices(obj: THREE.Object3D): number {
  let n = 0
  obj.traverse(c => {
    if (c instanceof THREE.Mesh && c.geometry) {
      n += c.geometry.attributes.position.count
    }
  })
  return n
}

function enableShadows(obj: THREE.Object3D) {
  obj.traverse(c => {
    if (c instanceof THREE.Mesh) {
      c.castShadow = true
      c.receiveShadow = true
    }
  })
}

const flatMat = new THREE.MeshStandardMaterial({ color: 0x88aaff, roughness: 0.6, metalness: 0.3, flatShading: true, side: THREE.DoubleSide })
const stdMat = new THREE.MeshStandardMaterial({ color: 0x88aaff, roughness: 0.6, metalness: 0.3, side: THREE.DoubleSide })

const LOAD_TIMEOUT = 60000

function loadModelImpl(options: ModelLoadOptions): Promise<ModelLoadResult> {
  return new Promise((resolve, reject) => {
    let timedOut = false
    const timer = setTimeout(() => {
      timedOut = true
      reject(new Error('加载超时'))
    }, LOAD_TIMEOUT)

    const done = (fn: (...args: any[]) => void) => (...args: any[]) => {
      if (timedOut) return
      clearTimeout(timer)
      fn(...args)
    }

    let format: ModelFormat
    try {
      format = options.format || detectFormat(options.url)
    } catch (e) {
      clearTimeout(timer)
      reject(e instanceof Error ? e : new Error(String(e)))
      return
    }

    const { url, autoCenter = true, autoScale = true, targetSize = 5, onProgress } = options
    const onError = done((err: unknown) => {
      const msg = err instanceof ErrorEvent ? err.message
        : err instanceof Error ? err.message
        : typeof err === 'string' ? err
        : 'unknown loading error'
      reject(new Error(msg))
    })
    const safeLoad = done((group: THREE.Group) => {
      try {
        const measurements = computeMeasurements(group)
        const origBox = new THREE.Box3().setFromObject(group)
        const origSize = origBox.getSize(new THREE.Vector3())
        const origMax = Math.max(origSize.x, origSize.y, origSize.z)
        if (autoCenter || autoScale) {
          const scale = origMax > 0 ? targetSize / origMax : 1
          group.scale.setScalar(scale)
          group.userData.inverseScale = origMax > 0 ? origMax / targetSize : 1
          modelCount++
        } else {
          group.userData.inverseScale = 1
        }
        resolve({ group, format, url, vertexCount: countVertices(group), faceCount: countFaces(group), measurements })
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)))
      }
    })

    if (format === 'stl') {
      const l = new STLLoader()
      l.load(url,
        g => {
          const mesh = new THREE.Mesh(g, flatMat)
          mesh.castShadow = true; mesh.receiveShadow = true
          const grp = new THREE.Group(); grp.add(mesh)
          safeLoad(grp)
        },
        x => onProgress?.(x.total > 0 ? x.loaded / x.total : 0),
        onError,
      )
    } else if (format === 'ply') {
      const l = new PLYLoader()
      l.load(url,
        g => {
          g.computeVertexNormals()
          const hasColors = g.attributes.color !== undefined
          const mat = new THREE.MeshStandardMaterial({
            color: hasColors ? 0xffffff : 0x88aaff,
            roughness: 0.6,
            metalness: 0.3,
            side: THREE.DoubleSide,
            flatShading: !hasColors,
            vertexColors: hasColors,
          })
          const mesh = new THREE.Mesh(g, mat)
          mesh.castShadow = true; mesh.receiveShadow = true
          const grp = new THREE.Group(); grp.add(mesh)
          safeLoad(grp)
        },
        x => onProgress?.(x.total > 0 ? x.loaded / x.total : 0),
        onError,
      )
    } else if (format === 'obj') {
      const l = new OBJLoader()
      l.load(url,
        obj => {
          obj.traverse(c => {
            if (c instanceof THREE.Mesh) {
              c.castShadow = true; c.receiveShadow = true
              if (!c.material) c.material = stdMat.clone()
            }
          })
          safeLoad(obj)
        },
        x => onProgress?.(x.total > 0 ? x.loaded / x.total : 0),
        onError,
      )
    } else if (format === 'gltf' || format === 'glb') {
      const l = new GLTFLoader()
      l.load(url,
        gltf => {
          enableShadows(gltf.scene)
          safeLoad(gltf.scene)
        },
        x => onProgress?.(x.total > 0 ? x.loaded / x.total : 0),
        onError,
      )
    } else if (format === 'fbx') {
      const l = new FBXLoader()
      l.load(url,
        group => {
          enableShadows(group)
          safeLoad(group)
        },
        x => onProgress?.(x.total > 0 ? x.loaded / x.total : 0),
        onError,
      )
    } else if (format === 'step' || format === 'stp') {
      fetch(url)
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          onProgress?.(0.3)
          return r.arrayBuffer()
        })
        .then(buf => {
          onProgress?.(0.6)
          return import('./STEPLoader').then(m => m.loadSTEP(buf))
        })
        .then(group => {
          onProgress?.(0.9)
          enableShadows(group)
          safeLoad(group)
        })
        .catch(onError)
    } else if (format === 'iges' || format === 'igs') {
      fetch(url)
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          return r.arrayBuffer()
        })
        .then(buf => import('./STEPLoader').then(m => m.loadIGES(buf)))
        .then(group => {
          enableShadows(group)
          safeLoad(group)
        })
        .catch(onError)
    } else if (format === 'brep') {
      fetch(url)
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          return r.arrayBuffer()
        })
        .then(buf => import('./STEPLoader').then(m => m.loadBREP(buf)))
        .then(group => {
          enableShadows(group)
          safeLoad(group)
        })
        .catch(onError)
    }
  })
}

export function loadModel(options: ModelLoadOptions): Promise<ModelLoadResult> {
  const key = `${options.url}|${options.format || ''}|${options.autoCenter}|${options.autoScale}|${options.targetSize}`
  const cached = cache.get(key)
  if (cached) return cached
  const promise = loadModelImpl(options)
  cache.set(key, promise)
  promise.catch(() => cache.delete(key))
  return promise
}

export function clearCache() {
  cache.clear()
  modelCount = 0
}
